<template>
  <article id="radiometric-calibration">
    <h1>辐射定标</h1>

    <section id="rad-calibration-intro">
      <h2>一、简介</h2>
      <p>
        &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;辐射定标是遥感影像处理流程中的第一步，主要将原始的灰度值影像（数字数值 DN）转换为具有物理意义的辐亮度影像（radiance）。该模块通过自动提取卫星载荷定标参数，实现快速、准确的辐射定标处理，是后续大气校正与反射率计算的基础。
      </p>
      <p>
        &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;本软件通过内置的卫星基础信息库（包括定标系数、增益、偏置、定标模型等），实现了对30余种国产陆地卫星传感器的一致性支持和全自动化处理。
      </p>
    </section>

    <section id="rad-calibration-interface">
      <h2>二、功能界面</h2>
      <div class="image-section">
        <div class="image-container">
          <img src="@/assets/documentation/fushe.png" alt="辐射校正界面" class="software-image" />
          <p class="image-caption">图4：辐射校正界面 - 将原始灰度值影像转换为具有物理意义的辐亮度影像</p>
        </div>
      </div>
      <p>界面组成：</p>
      <ul>
        <li>输入路径选择框（支持单景或多景影像）</li>
        <li>卫星识别与定标信息显示（自动填充）</li>
        <li>影像信息预览（波段数、分辨率、投影等）</li>
        <li>输出路径设置</li>
        <li>"开始定标"按钮与进度提示框</li>
      </ul>
      <p class="note">📊 辐射定标是遥感影像处理的第一步，通过自动提取卫星载荷定标参数，将原始数字数值转换为具有物理意义的辐亮度值。</p>
    </section>

    <section id="rad-calibration-params">
      <h2>三、用户参数</h2>
      <table>
        <thead>
          <tr>
            <th>参数项</th>
            <th>含义</th>
            <th>设置方式</th>
            <th>示例值</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>输入影像路径</td>
            <td>原始影像所在路径</td>
            <td>自动或手动选择</td>
            <td>D:\data\GF1\raw.tif</td>
          </tr>
          <tr>
            <td>卫星/传感器类型</td>
            <td>自动识别卫星及传感器</td>
            <td>自动读取元数据</td>
            <td>GF-1_WFV</td>
          </tr>
          <tr>
            <td>定标系数</td>
            <td>增益（Gain）与偏置（Offset）</td>
            <td>自动匹配参数库</td>
            <td>G=[0.012] O=[1.0]</td>
          </tr>
          <tr>
            <td>输出路径</td>
            <td>定标结果保存路径</td>
            <td>手动选择或默认生成</td>
            <td>D:\data\GF1\rad\</td>
          </tr>
        </tbody>
      </table>
      <p class="note">✅ 注意：所有参数均可自动完成配置，用户无需手动输入</p>
    </section>

    <section id="rad-calibration-steps">
      <h2>四、操作步骤</h2>
      <ol>
        <li>
          <strong>导入原始影像</strong>
          <ul>
            <li>点击"选择影像路径"或直接拖拽影像至输入框；</li>
            <li>支持多景批处理，格式支持 GeoTIFF、HDF、JPEG2000 等。</li>
          </ul>
        </li>
        <li>
          <strong>自动识别元数据</strong>
          <p>软件将自动解析影像头文件或元数据 XML，识别卫星平台、传感器、拍摄时间等。</p>
        </li>
        <li>
          <strong>提取定标参数</strong>
          <ul>
            <li>软件调用内置"卫星基础信息库"，匹配该影像对应的增益/偏置；</li>
            <li>若存在不一致项，将进行提示与人工确认。</li>
          </ul>
        </li>
        <li>
          <strong>执行辐射定标</strong>
          <p>按照 DN → Radiance 模型，逐波段完成转换；</p>
          <p>支持 CPU 多线程加速与内存优化处理。</p>
        </li>
        <li>
          <strong>保存输出结果</strong>
          <ul>
            <li>输出格式为 GeoTIFF，命名方式与原始影像一致，追加 _rad 后缀；</li>
            <li>同时生成 .log 日志文件记录处理信息。</li>
          </ul>
        </li>
      </ol>
    </section>

    <section id="rad-calibration-results">
      <h2>五、用户结果</h2>
      <table>
        <thead>
          <tr>
            <th>输出文件名</th>
            <th>文件类型</th>
            <th>内容说明</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>GF1_WFV_20230510_L1_rad.tif</td>
            <td>影像文件</td>
            <td>定标后辐亮度影像，单位：W/m²/sr/μm</td>
          </tr>
          <tr>
            <td>GF1_WFV_20230510_L1_rad.log</td>
            <td>日志文件</td>
            <td>包含定标参数、处理时间、影像信息</td>
          </tr>
          <tr>
            <td>meta_GF1_WFV_rad.json</td>
            <td>元信息文件</td>
            <td>处理参数及版本记录（可选）</td>
          </tr>
        </tbody>
      </table>
      <p class="note">✅   输出影像可直接作为后续"大气校正模块"的输入文件。</p>
      <p class="note">✅ 日志文件可用于批处理追溯与精度分析。</p>
    </section>
  </article>
</template>

<script>
export default {
  name: 'RadiationCalibration',
  props: {
    targetSectionId: {
      type: String,
      default: null,
    },
  },
  watch: {
    targetSectionId(newId) {
      if (newId) {
        this.$emit('scroll-to-section', newId);
      }
    },
  },
  mounted() {
    if (this.targetSectionId) {
      this.$emit('scroll-to-section', this.targetSectionId);
    }
  },
};
</script>

<style scoped>
/* 图片展示样式 */
.image-section {
  margin: 30px 0;
  display: flex;
  flex-direction: column;
  gap: 30px;
}

.image-container {
  text-align: center;
  background: rgba(var(--card-bg), 0.8);
  border-radius: 12px;
  padding: 20px;
  box-shadow: 0 4px 20px rgba(var(--primary), 0.1);
  border: 1px solid rgba(var(--primary), 0.1);
}

.software-image {
  max-width: 100%;
  height: auto;
  border-radius: 8px;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.2);
  transition: transform 0.3s ease, box-shadow 0.3s ease;
}

.software-image:hover {
  transform: scale(1.02);
  box-shadow: 0 6px 25px rgba(0, 0, 0, 0.3);
}

.image-caption {
  margin-top: 15px;
  font-size: 14px;
  color: var(--text-secondary);
  font-style: italic;
  line-height: 1.5;
}

/* 添加计数器重置 */
ol {
  counter-reset: list-item;
}

/* 重用 Document.vue 中的表格和列表样式 */
table {
  width: 100%;
  border-collapse: collapse;
  margin: 25px 0;
  font-size: 15px;
  text-align: left;
  box-shadow: 0 0 15px rgba(var(--primary), 0.1);
  border-radius: 8px;
  overflow: hidden;
}

th,
td {
  padding: 12px 15px;
  border: 1px solid rgba(var(--primary), 0.2); /* 主题色边框 */
  color: var(--text-primary);
}

thead tr {
  background-color: rgba(var(--primary), 0.2); /* 表头背景色 */
  color: var(--primary);
  text-shadow: 0 0 5px rgba(var(--primary), 0.5);
  font-weight: bold;
}

tbody tr {
  background-color: rgba(var(--card-bg), 0.6); /* 行背景色 */
}

tbody tr:nth-of-type(even) {
  background-color: rgba(var(--card-bg), 0.8); /* 隔行变色 */
}

tbody tr:hover {
  background-color: rgba(var(--primary), 0.1);
  color: var(--primary);
  text-shadow: 0 0 3px rgba(var(--primary), 0.3);
}

/* List Styles (Ordered and Unordered) */
ol,
ul {
  list-style: none;
  padding-left: 20px; /* 适当缩进 */
  margin: 20px 0;
}

ol li,
ul li {
  margin-bottom: 10px;
  color: var(--text-primary);
  line-height: 1.6;
  position: relative;
}

ol li {
  counter-increment: list-item;
}

ol li strong,
ul li strong {
  color: var(--primary); /* 强调文本颜色 */
  text-shadow: 0 0 5px rgba(var(--primary), 0.4);
}

ol li::before {
  content: counter(list-item) ". ";
  color: var(--secondary); /* 编号颜色 */
  font-weight: bold;
  position: absolute;
  left: -25px;
  top: 0;
  width: 20px;
  text-align: right;
  text-shadow: 0 0 5px rgba(var(--secondary), 0.5);
}

ul li::before {
  content: "\2705"; /* 复选框图标 */
  color: var(--secondary); /* 图标颜色 */
  font-size: 1.1em;
  position: absolute;
  left: -50px;
  top: 0;
  text-shadow: 0 0 5px rgba(var(--secondary), 0.5);
}

ul li p {
  margin-left: 0; /* 重置列表项内段落的左外边距 */
  margin-bottom: 5px; /* 调整列表项内段落间距 */
}

p.note {
  background-color: rgba(var(--secondary), 0.1);
  border-left: 5px solid var(--secondary);
  padding: 10px 15px;
  margin-top: 20px;
  border-radius: 4px;
  color: var(--text-primary);
  font-size: 14px;
}
</style>